import { useId, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import {
  COMMITTEE_ORGANIZATION_TYPE_LABELS,
  CommitteeFilingJurisdiction,
  CommitteeOrganizationType,
  FILING_JURISDICTION_LABELS,
  registrationNeedsAttention,
  type CommitteeAttestation,
  type CommitteeAttestationInput,
  type CommitteeFacts,
  type CommitteeFactsDto,
} from '@signalvote/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCommitteeFacts, useSaveCommitteeFacts } from '@/hooks/queries';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { committeeFactError } from './committeeFactErrors';
import { ExpressAttestation, isSigned } from './ExpressAttestation';
import { REQUIRED_COMMITTEE_FACTS } from './expressProgress';
import { ExpressStep } from './ExpressStep';
import { FIELD_CLASS } from './fieldClass';
import type { ExpressStepViewProps } from './expressStepViews';

type FactKey = keyof CommitteeFacts;
type FactValues = Record<FactKey, string>;

interface FactField {
  key: FactKey;
  label: string;
  hint?: string;
  type?: 'text' | 'email' | 'tel' | 'date' | 'url';
  /** Spans both columns from tablet width up. */
  wide?: boolean;
  multiline?: boolean;
  choices?: Record<string, string>;
  /** Shown only while this is true of the answers. */
  when?: (values: FactValues) => boolean;
  /** The server fills this from what the campaign already told us, so a value here isn't the person's answer. */
  suggested?: boolean;
}

const REQUIRED_FIELDS: Record<(typeof REQUIRED_COMMITTEE_FACTS)[number], FactField> = {
  legalName: { key: 'legalName', label: 'Committee name as filed', hint: 'Exactly as on your filing.', wide: true },
  ein: { key: 'ein', label: 'EIN', hint: 'Your committee’s federal tax ID, like 12-3456789.' },
  filingPhone: { key: 'filingPhone', label: 'Phone on your filing', type: 'tel' },
  filingEmail: { key: 'filingEmail', label: 'Email on your filing', type: 'email', wide: true },
};

const OPTIONAL_FIELDS: FactField[] = [
  { key: 'organizationType', label: 'What kind of committee', choices: COMMITTEE_ORGANIZATION_TYPE_LABELS },
  {
    key: 'organizationTypeOther',
    label: 'Which kind',
    when: (values) => values.organizationType === CommitteeOrganizationType.Other,
  },
  { key: 'filingJurisdiction', label: 'Where you file', choices: FILING_JURISDICTION_LABELS },
  {
    key: 'committeeFilerId',
    label: 'Committee ID',
    hint: 'Your FEC or state filer ID.',
    when: (values) => values.filingJurisdiction !== CommitteeFilingJurisdiction.NotSure,
  },
  { key: 'officeSought', label: 'Office or race', suggested: true },
  { key: 'electionDate', label: 'Election date', type: 'date', suggested: true },
  { key: 'treasurerName', label: 'Treasurer' },
  {
    key: 'verificationContactName',
    label: 'Who gets the verification code',
    hint: 'A code that confirms the committee goes to the email or phone on your filing. Name the person who reads it.',
    suggested: true,
  },
  { key: 'street', label: 'Street address', wide: true },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP' },
  { key: 'website', label: 'Website', type: 'url', suggested: true },
  {
    key: 'optInDescription',
    label: 'How people agreed to hear from you',
    hint: 'In your own words, such as “they signed up on our website.”',
    multiline: true,
    wide: true,
    suggested: true,
  },
  { key: 'sampleMessage2', label: 'Another text you might send', multiline: true, wide: true },
];

function valuesOf(facts: CommitteeFacts): FactValues {
  return Object.fromEntries(Object.entries(facts).map(([key, value]) => [key, value ?? ''])) as FactValues;
}

function factsOf(values: FactValues): CommitteeFacts {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value.trim() || null])) as unknown as CommitteeFacts;
}

/**
 * A facts save withdraws any signature, since it covered the facts as they were. The signer is
 * kept, so signing again is one tick.
 */
function unconfirmed(signer: CommitteeAttestation | null | undefined): CommitteeAttestationInput | null {
  if (!signer) return null;
  const { signerName, signerRole, signerRoleOther, signerEmail } = signer;
  return { signerName, signerRole, signerRoleOther, signerEmail, confirmed: false };
}

/**
 * About your committee: what carriers need to register a political sender, field for field as the
 * Vendor's form asks. Each answer saves as the person moves on, one save after another so every save
 * carries the token the last one returned. With nothing left to pay for (details turned down after
 * paying), the step ends with the signature itself.
 */
export function CommitteeStep(props: ExpressStepViewProps) {
  // The suggestions follow the Text send, which earlier steps change after the facts were first read.
  const committee = useCommitteeFacts({ refetchOnMount: 'always' });
  if (!committee.data || !committee.isFetchedAfterMount) {
    return (
      <ExpressStep step={props.step} onBack={props.onBack} error={committee.isError ? 'We couldn’t load your committee details. Refresh to try again.' : null}>
        {null}
      </ExpressStep>
    );
  }
  return <CommitteeForm {...props} committee={committee.data} />;
}

function CommitteeForm({ step, onBack, onNext, committee }: ExpressStepViewProps & { committee: CommitteeFactsDto }) {
  const id = useId();
  const navigate = useNavigate();
  const [values, setValues] = useState(() => valuesOf(committee.facts));
  const fixing = !onNext;
  const [moreOpen, setMoreOpen] = useState(
    () => fixing || OPTIONAL_FIELDS.some((f) => !f.suggested && values[f.key].trim() !== ''),
  );
  const saved = useRef(values);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const { mutateAsync: saveFacts, isPending: saving } = useSaveCommitteeFacts('facts');
  const queue = useRef<Promise<boolean>>(Promise.resolve(true));
  const queryClient = useQueryClient();
  const latestCommittee = () => queryClient.getQueryData<CommitteeFactsDto>(queryKeys.messagingProfile.committee());

  const save = () => {
    const sending = values;
    queue.current = queue.current.then(async () => {
      if (Object.keys(sending).every((key) => sending[key as FactKey] === saved.current[key as FactKey])) return true;
      try {
        await saveFacts({ facts: factsOf(sending), attestation: unconfirmed(latestCommittee()?.attestation) });
        saved.current = sending;
        setErrors({});
        setFormError(null);
        return true;
      } catch (error) {
        const refusal = committeeFactError(error);
        if (refusal.field) setErrors({ [refusal.field]: refusal.message });
        else setFormError(refusal.message);
        return false;
      }
    });
    return queue.current;
  };

  const set = (key: FactKey, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const complete = REQUIRED_COMMITTEE_FACTS.every((key) => values[key].trim() !== '');
  const blocked = Object.values(errors).some(Boolean);
  const next = async () => {
    if (await save()) onNext?.();
  };

  const field = (f: FactField) => {
    if (f.when && !f.when(values)) return null;
    const inputId = `${id}-${f.key}`;
    const error = errors[f.key];
    const describedBy = [f.hint && `${inputId}-hint`, error && `${inputId}-error`].filter(Boolean).join(' ') || undefined;
    const common = {
      id: inputId,
      name: f.key,
      value: values[f.key],
      'aria-invalid': error ? true : undefined,
      'aria-describedby': describedBy,
    };
    let control: ReactNode;
    if (f.choices) {
      control = (
        <select
          {...common}
          className={FIELD_CLASS}
          onChange={(e) => set(f.key, e.target.value)}
          onBlur={() => void save()}
        >
          <option value="">Choose…</option>
          {Object.entries(f.choices).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      );
    } else if (f.multiline) {
      control = (
        <textarea {...common} rows={3} className={FIELD_CLASS} onChange={(e) => set(f.key, e.target.value)} onBlur={() => void save()} />
      );
    } else {
      control = (
        <Input {...common} type={f.type ?? 'text'} onChange={(e) => set(f.key, e.target.value)} onBlur={() => void save()} />
      );
    }
    return (
      <div key={f.key} className={cn('space-y-1.5', f.wide && 'sm:col-span-2')}>
        <Label htmlFor={inputId}>{f.label}</Label>
        {control}
        {f.hint && (
          <p id={`${inputId}-hint`} className="text-xs text-muted-foreground">
            {f.hint}
          </p>
        )}
        {error && (
          <p id={`${inputId}-error`} className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <ExpressStep
      step={step}
      onBack={onBack}
      error={formError}
      primary={
        fixing
          ? { label: 'Back to Orders', disabled: !isSigned(committee), onPress: () => void navigate({ to: '/app/work-orders' }) }
          : { label: 'Continue', disabled: !complete || blocked, busy: saving, busyLabel: 'Saving…', onPress: () => void next() }
      }
    >
      {registrationNeedsAttention(committee.status) && (
        <div role="note" className="mb-8 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">Carriers need a change before your texts can go out.</p>
          {committee.rejectionReason && <p className="mt-1">{committee.rejectionReason}</p>}
        </div>
      )}
      <fieldset className="space-y-4">
        <legend className="mb-4 text-sm font-semibold text-foreground">We need these to register you</legend>
        <div className="grid gap-4 sm:grid-cols-2">{REQUIRED_COMMITTEE_FACTS.map((key) => field(REQUIRED_FIELDS[key]))}</div>
      </fieldset>
      <div className="mt-8">
        <button
          type="button"
          aria-expanded={moreOpen}
          aria-controls={`${id}-more`}
          onClick={() => setMoreOpen((open) => !open)}
          className="flex min-h-11 items-center gap-2 text-sm font-semibold text-foreground"
        >
          <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', moreOpen && 'rotate-180')} />
          Add more details (optional)
        </button>
        {moreOpen && (
          <div id={`${id}-more`} className="mt-4 space-y-4">
            <p className="text-sm text-muted-foreground">Anything you add here saves us asking later.</p>
            <div className="grid gap-4 sm:grid-cols-2">{OPTIONAL_FIELDS.map(field)}</div>
          </div>
        )}
      </div>
      {fixing && (
        <div className="mt-10">
          <ExpressAttestation beforeSign={save} />
        </div>
      )}
    </ExpressStep>
  );
}
